import jsPsychFullscreen from "@jspsych/plugin-fullscreen";

import { restoreCursor, showCursor } from "../../lib/cursorVisibility";
import { showToastMessage } from "../../lib/toast";

const RESTORE_FULLSCREEN_MESSAGE = "Click here to return to fullscreen.";
// Stays up until fullscreen is restored (or the watchdog is disarmed) - matches the TTL
// disconnect toast's "don't auto-dismiss" pattern, since missing it means staying windowed.
const RESTORE_FULLSCREEN_DURATION_MS = 24 * 60 * 60 * 1000;
const CURSOR_REQUESTER_ID = "fullscreenPrompt";

let dismissFullscreenPrompt = null;

function clearFullscreenPrompt() {
  if (!dismissFullscreenPrompt) return;
  dismissFullscreenPrompt();
  dismissFullscreenPrompt = null;
  restoreCursor(CURSOR_REQUESTER_ID);
}

function handleFullscreenChange() {
  if (document.fullscreenElement) {
    clearFullscreenPrompt();
    return;
  }

  if (dismissFullscreenPrompt) return; // Already showing

  showCursor(CURSOR_REQUESTER_ID); // Otherwise gameplay's "hide-cursor" rule hides this toast's pointer
  dismissFullscreenPrompt = showToastMessage(RESTORE_FULLSCREEN_MESSAGE, {
    durationMs: RESTORE_FULLSCREEN_DURATION_MS,
    centered: true,
    onClick: () => {
      // Called directly from the click event, so this still has a fresh user gesture -
      // requestFullscreen() throws if called without one (e.g. from an awaited callback).
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("[fullscreen] failed to restore fullscreen:", err);
      });
    },
  });
}

/**
 * Arms a watchdog that prompts the experimenter to restore fullscreen (with a single click)
 * whenever it's lost mid-gameplay - whether from pressing Esc, alt-tabbing, or the TTL device's
 * reconnect flow forcing an exit (see webUsbFtdi.js). Call stopFullscreenWatchdog() before any
 * *intentional* exit (e.g. the end-of-experiment exitFullscreenTrial) so the prompt doesn't nag
 * on screens that are supposed to be windowed.
 */
export function startFullscreenWatchdog() {
  document.addEventListener("fullscreenchange", handleFullscreenChange);
  handleFullscreenChange(); // Covers the case where fullscreen was already lost when armed
}

/** Disarms the watchdog started by startFullscreenWatchdog() and clears any visible prompt. */
export function stopFullscreenWatchdog() {
  document.removeEventListener("fullscreenchange", handleFullscreenChange);
  clearFullscreenPrompt();
}

/**
 * Trial to enter fullscreen mode.
 * Any trials after this one will be run while the app/browser is in fullscreen.
 */
export const enterFullscreenTrial = {
  type: jsPsychFullscreen,
  fullscreen_mode: true,
  on_finish: (data) => {
    // Record some additional information about the user's screen
    data.screen_width = screen.width;
    data.screen_height = screen.height;
    data.screen_pixel_ratio = window.devicePixelRatio;

    startFullscreenWatchdog();
  },
};

/**
 * Trial to exit fullscreen mode.
 * Any trials after this one will NOT be run while the app/browser is in fullscreen.
 */
export const exitFullscreenTrial = {
  type: jsPsychFullscreen,
  fullscreen_mode: false,
  // Runs before the plugin's own exitFullscreen() call, so the watchdog doesn't mistake this
  // intentional exit for a lost-fullscreen event and pop the restore prompt right back up.
  on_start: () => stopFullscreenWatchdog(),
};
